import { useCallback, useEffect, useState, type RefObject } from "react";

// Estado das setas de um carrossel de rolagem: só mostra as setas quando o conteúdo
// não cabe na largura (senão não há nada para rolar) e desativa a seta do lado em
// que já chegou ao fim. `dependency` refaz a medida quando o conteúdo muda.
export function useCarousel(track: RefObject<HTMLDivElement | null>, dependency: unknown) {
  const [state, setState] = useState({ overflow: false, prev: false, next: false });

  const update = useCallback(() => {
    const el = track.current;
    if (!el) return;
    const overflow = el.scrollWidth > el.clientWidth + 4;
    setState({
      overflow,
      prev: overflow && el.scrollLeft > 4,
      next: overflow && el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
    });
  }, [track]);

  useEffect(() => {
    const el = track.current;
    if (!el) return;
    update();
    el.addEventListener("scroll", update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      observer.disconnect();
    };
  }, [track, update, dependency]);

  // Rola quase uma tela inteira por clique (um pouco menos, para o próximo item aparecer).
  const scrollBy = useCallback((direction: 1 | -1) => {
    const el = track.current;
    if (el) el.scrollBy({ left: direction * el.clientWidth * 0.9, behavior: "smooth" });
  }, [track]);

  return { ...state, scrollBy };
}

// Avanço automático de um carrossel: a cada `interval` ms rola um item e, ao chegar ao
// fim, volta ao começo. Pausa enquanto o ponteiro ou o foco estão sobre o carrossel,
// enquanto a pessoa toca/arrasta (e alguns segundos depois) e com a aba escondida.
// Não roda para quem pediu menos movimento no sistema.
export function useAutoplay(track: RefObject<HTMLDivElement | null>, enabled: boolean, interval = 5000) {
  useEffect(() => {
    const el = track.current;
    if (!el || !enabled) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let hovering = false;
    let focused = false;
    let pausedUntil = 0;
    const holdAfterTouch = () => { pausedUntil = Date.now() + 8000; };
    const enter = () => { hovering = true; };
    const leave = () => { hovering = false; };
    const focusIn = () => { focused = true; };
    const focusOut = () => { focused = false; };

    el.addEventListener("pointerenter", enter);
    el.addEventListener("pointerleave", leave);
    el.addEventListener("focusin", focusIn);
    el.addEventListener("focusout", focusOut);
    el.addEventListener("touchstart", holdAfterTouch, { passive: true });
    el.addEventListener("wheel", holdAfterTouch, { passive: true });

    const timer = window.setInterval(() => {
      if (hovering || focused || document.hidden || Date.now() < pausedUntil) return;
      if (el.scrollWidth <= el.clientWidth + 4) return;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
      if (atEnd) {
        el.scrollTo({ left: 0, behavior: "smooth" });
        return;
      }
      const first = el.firstElementChild as HTMLElement | null;
      const gap = parseFloat(getComputedStyle(el).columnGap) || 0;
      const step = first ? first.offsetWidth + gap : el.clientWidth * 0.9;
      el.scrollBy({ left: step, behavior: "smooth" });
    }, interval);

    return () => {
      window.clearInterval(timer);
      el.removeEventListener("pointerenter", enter);
      el.removeEventListener("pointerleave", leave);
      el.removeEventListener("focusin", focusIn);
      el.removeEventListener("focusout", focusOut);
      el.removeEventListener("touchstart", holdAfterTouch);
      el.removeEventListener("wheel", holdAfterTouch);
    };
  }, [track, enabled, interval]);
}
